<script lang="ts">
	import { createInfiniteQuery } from '@tanstack/svelte-query'
	import DetailedSelect from './DetailedSelect.svelte'
	import {
		DetailedSelectItemableElementType,
		type ReleaseVersionSelectable
	} from '@repo/commons/models'
	import { elementToDetailedSelectItem } from '@repo/commons/utils'
	import type { DetailedSelectItem } from '@repo/commons/models'
	import type { PaginatedResponse } from '@repo/commons/models'
	import { DEFAULT_PAGE_SIZE } from '@repo/commons/constants'
	import type { QueryKey } from '@tanstack/svelte-query'

	interface Props {
		queryKey: QueryKey
		fetcher: (page: number, size: number) => Promise<PaginatedResponse<ReleaseVersionSelectable>>
		enabled?: boolean
		pageSize?: number
		selectedItem?: DetailedSelectItem | null
		placeholder?: string
		position?: 'left' | 'right'
		label?: string
		error?: string
		isRequired?: boolean
		disabled?: boolean
		isPending?: boolean
		isError?: boolean
		prefixIcon?: { name: string; class?: string; size?: string } | null
		onSelect?: ((item: DetailedSelectItem | null) => void) | null
		onItemsChange?:
			| ((items: DetailedSelectItem[], draftItem: DetailedSelectItem | null) => void)
			| null
	}

	let {
		queryKey,
		fetcher,
		enabled = true,
		pageSize = DEFAULT_PAGE_SIZE,
		selectedItem = null,
		placeholder = 'Select version',
		position = 'right',
		label = '',
		error = '',
		isRequired = false,
		disabled = false,
		isPending = $bindable(false),
		isError = $bindable(false),
		prefixIcon = { name: 'version', size: 'md', class: 'text-[#002C4D80]' },
		onSelect = null,
		onItemsChange = null
	}: Props = $props()

	const releasesQuery = $derived(
		createInfiniteQuery({
			queryKey,
			queryFn: ({ pageParam }) => fetcher(pageParam as number, pageSize),
			initialPageParam: 0,
			getNextPageParam: (lastPage, allPages, lastPageParam) => {
				const releaseCount = lastPage.data.filter((v) => !v.isLatestDraft).length
				if (releaseCount < pageSize) return undefined
				const loaded = allPages.reduce(
					(sum, p) => sum + p.data.filter((v) => !v.isLatestDraft).length,
					0
				)
				if (lastPage.totalItems > 0 && loaded >= lastPage.totalItems) return undefined
				return (lastPageParam as number) + 1
			},
			enabled
		})
	)

	const allVersions = $derived(($releasesQuery.data?.pages ?? []).flatMap((p) => p.data))

	const draftVersion = $derived(allVersions.find((v) => v.isLatestDraft) ?? null)

	const draftItem = $derived(
		draftVersion
			? elementToDetailedSelectItem(draftVersion, DetailedSelectItemableElementType.RELEASE_VERSION)
			: null
	)

	const items = $derived.by((): DetailedSelectItem[] => {
		const releases = allVersions
			.filter((v) => !v.isLatestDraft)
			.sort((a, b) => (b.versionNumber ?? 0) - (a.versionNumber ?? 0))
			.map((v) => elementToDetailedSelectItem(v, DetailedSelectItemableElementType.RELEASE_VERSION))
		return [...(draftItem ? [draftItem] : []), ...releases]
	})

	const resolvedSelectedItem = $derived.by((): DetailedSelectItem | null => {
		if (!selectedItem) return null
		return items.find((item) => item.title === selectedItem.title) ?? selectedItem
	})

	$effect(() => {
		isPending = $releasesQuery.isPending
	})

	$effect(() => {
		isError = $releasesQuery.isError
	})

	$effect(() => {
		onItemsChange?.(items, draftItem)
	})
</script>

<DetailedSelect
	{items}
	selectedItem={resolvedSelectedItem}
	{placeholder}
	{position}
	{label}
	{error}
	{isRequired}
	disabled={disabled || $releasesQuery.isPending}
	isLoading={$releasesQuery.isPending}
	hasMore={$releasesQuery.hasNextPage ?? false}
	loadingMore={$releasesQuery.isFetchingNextPage}
	onLoadMore={() => void $releasesQuery.fetchNextPage()}
	remoteItems
	{onSelect}
	{prefixIcon}
/>
